var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { Tooltip } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import moment from 'moment';
import React, { useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { getMedicationsToRequestByPatientAuthKeys } from '../Async/MedicationsSummaryAsync';
import { createPharmacyRequest, searchPharmacyRequests } from '../Async/PharmacyRequestsAsync';
import { PermissionsCheck } from '../Components/Auth/PermissionsCheck';
import { Button, ButtonMode, ButtonSize } from '../Components/Button';
import { ErrorBoundary } from '../Components/ErrorBoundary';
import { ModalAnimationStyle } from '../Components/FullScreenModal';
import { CloseIcon, FilterIcon, PlusIcon } from '../Components/Icons';
import { LayoutCentral } from '../Components/LayoutCentral';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { TwoPanelLayout } from '../Components/TwoPanelLayout';
import { displayError, displayMessage } from '../Config/ApplicationConfig';
import { useAsync } from '../Hooks/useAsync';
import { MedicationsPermissions } from '../MedicationsPermissions';
import { routeForPharmacyRequest } from '../Routes';
import { defaultAuthoringSystem, emptyGuid, selfAuthoringSystemForeignKey } from '../Shared/Constants';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { useHttpConfig } from '../Shared/HttpRequestExecutor';
import { iconLight, primary, white } from '../Styles/Colours';
import { muiMedicationsTheme } from '../Styles/Theme';
import { AddPharmacyRequestForm } from './AddPharmacyRequestForm/AddPharmacyRequestForm';
import styles from './PharmacyRequests.module.css';
import { PharmacyRequestsSummary } from './PharmacyRequestsSummary';
import { convertFilterToQueryObject, PharmacyRequestsSummaryFilter, usePharmacyRequestsListFilter } from './PharmacyRequestsSummaryFilter';
import { useMedicationSettings } from '../Config/MedicationConfig';
import { PharmacyRequestHistorySummary } from './PharmacyRequestHistorySummary';
// Enum to facilitate ease of use
export var PharmacyRequestResponseTypes;
(function (PharmacyRequestResponseTypes) {
    PharmacyRequestResponseTypes["Success"] = "success";
    PharmacyRequestResponseTypes["ValidationError"] = "validationError";
    PharmacyRequestResponseTypes["ServerError"] = "serverError";
})(PharmacyRequestResponseTypes || (PharmacyRequestResponseTypes = {}));
var PharmacyRequestWizardView;
(function (PharmacyRequestWizardView) {
    PharmacyRequestWizardView["None"] = "none";
    PharmacyRequestWizardView["NewPharmacyRequestWizard"] = "newPharmacyRequestWizard";
})(PharmacyRequestWizardView || (PharmacyRequestWizardView = {}));
export var PharmacyRequests = function (props) {
    return (React.createElement(ErrorBoundary, null,
        React.createElement(ThemeProvider, { theme: muiMedicationsTheme },
            React.createElement(Requests, __assign({}, props)))));
};
export var getOnCreatePharmacyRequest = function (props) {
    return function (medications) {
        if (!medications.pickupLocation) {
            console.error('No pickup location set');
            throw Error('No pickup location set');
        }
        var createPharmacyRequestData = {
            authoringSystem: defaultAuthoringSystem,
            authoringSystemForeignKey: selfAuthoringSystemForeignKey,
            patient: props.patient,
            service: props.service,
            pickupLocation: __assign({ id: emptyGuid }, medications.pickupLocation),
            generatedOn: moment().format(ValidDateFormats.NodeDateTimeTimezone),
            startsOn: moment().format(ValidDateFormats.IsoFormat),
            expiresOn: medications.expiresOn === ''
                ? ''
                : moment(medications.expiresOn, ValidDateFormats.NodeDateTimeTimezone).format(ValidDateFormats.IsoFormat),
            requestItems: medications.requestItems
                .filter(function (it) { return it.selected; })
                .map(function (it) { return ({
                notes: it.notes,
                patientMedication: it.patientMedication
            }); })
        };
        return createPharmacyRequest(createPharmacyRequestData);
    };
};
var Requests = function (props) {
    var _a, _b;
    var httpConfig = useHttpConfig();
    var medicationSettings = useMedicationSettings();
    var pharmacyRequestTerm = medicationSettings.settings.pharmacyRequestTerm;
    var encounterPlaceTerm = medicationSettings.settings.encounterPlaceTerm;
    var rootUrl = props.baseUrl;
    var newPharmacyRequestUrl = routeForPharmacyRequest(rootUrl);
    var displayNewPharmacyRequestWizard = useRouteMatch(newPharmacyRequestUrl);
    var location = useLocation();
    var animationStyle = ((_a = location.state) === null || _a === void 0 ? void 0 : _a.hideLoadAnimation)
        ? ModalAnimationStyle.None
        : ModalAnimationStyle.OpenAndClose;
    var goBackToMedications = !!((_b = location.state) === null || _b === void 0 ? void 0 : _b.onCloseRouteToMedications);
    var history = useHistory();
    var showAddPharmacyRequest = function () {
        goBackToMedications
            ? props.routeToMedications()
            : props.headlessMode
                ? setPharmacyRequestWizardView(PharmacyRequestWizardView.NewPharmacyRequestWizard)
                : history.push(newPharmacyRequestUrl);
    };
    var nagivateBackTo = function () {
        goBackToMedications
            ? props.routeToMedications()
            : props.headlessMode
                ? setPharmacyRequestWizardView(PharmacyRequestWizardView.None)
                : history.push(rootUrl);
    };
    var _c = __read(useState(PharmacyRequestWizardView.None), 2), pharmacyRequestWizardView = _c[0], setPharmacyRequestWizardView = _c[1];
    var _d = __read(useState(false), 2), filterOpen = _d[0], setFilterOpen = _d[1];
    var _e = __read(useState(null), 2), pharmacyRequestHistory = _e[0], setPharmacyRequestHistory = _e[1];
    var pharmacyRequestsAsync = useAsync({
        httpConfig: httpConfig,
        getApi: searchPharmacyRequests
    });
    var pharmacyRequests = pharmacyRequestsAsync.data;
    var filterState = usePharmacyRequestsListFilter(function (filter) {
        return pharmacyRequestsAsync.getApi(props.patient.authoringSystem, props.patient.authoringSystemForeignKey, filter.limit === 0 ? 0 : filter.limit + 1, convertFilterToQueryObject(filter));
    });
    var getContents = function () {
        var _a;
        if (pharmacyRequestHistory) {
            {
                return (React.createElement("div", { className: styles.pharmacyRequestHistorySummary },
                    React.createElement(PharmacyRequestHistorySummary, { pharmacyRequestTerm: pharmacyRequestTerm, pharmacyRequestId: pharmacyRequestHistory.id, pharmacyRequestNumber: pharmacyRequestHistory.requestNumber, onCloseHistoryClicked: function () { return setPharmacyRequestHistory(null); } })));
            }
        }
        return (React.createElement("div", { className: styles.container },
            React.createElement("div", { className: styles.header },
                React.createElement("div", { className: styles.title }, pharmacyRequestTerm === null || pharmacyRequestTerm === void 0 ? void 0 : pharmacyRequestTerm.pluralTerm),
                React.createElement("span", { className: styles.addPharmacyRequest },
                    React.createElement(PermissionsCheck, { permission: MedicationsPermissions.WritePharmacyRequest },
                        React.createElement(Button, { startIcon: React.createElement(PlusIcon, { color: white }), type: 'button', testId: 'add-pharmacy-requests', onClick: showAddPharmacyRequest, size: ButtonSize.small },
                            "Add ", (_a = pharmacyRequestTerm === null || pharmacyRequestTerm === void 0 ? void 0 : pharmacyRequestTerm.singularTerm) === null || _a === void 0 ? void 0 :
                            _a.toLowerCase()))),
                React.createElement(Tooltip, { title: 'Filter', placement: 'bottom' },
                    React.createElement("span", { className: styles.iconButton },
                        React.createElement(Button, { testId: 'button-filter', onClick: function () { return setFilterOpen(!filterOpen); }, mode: ButtonMode.text, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true },
                            React.createElement(FilterIcon, { color: filterOpen ? primary : iconLight }))))),
            pharmacyRequestsAsync.data === null || pharmacyRequestsAsync.loadCount === 0 ? (React.createElement(LayoutCentral, null,
                React.createElement(LoadingPlaceholder, null))) : (React.createElement(PharmacyRequestsSummary, __assign({}, props, { onUpdatedPharmacyRequests: filterState.refresh, pharmacyRequests: pharmacyRequests ? pharmacyRequests.pharmacyRequests : [], filterPanelOpen: filterOpen, showAddPharmacyRequestWizard: showAddPharmacyRequest, pharmacyRequestTerm: pharmacyRequestTerm, encounterPlaceTerm: encounterPlaceTerm, showPharmacyRequestHistory: function (pharmacyRequest) { return setPharmacyRequestHistory(pharmacyRequest); }, medicationNameFilter: filterState.state.medicationName })))));
    };
    var getLeftPanel = function () {
        return (React.createElement("div", { className: styles.leftContainer },
            React.createElement("div", { className: styles.header },
                React.createElement("div", { className: styles.title }, "Filters"),
                React.createElement(Tooltip, { title: 'Close', placement: 'bottom' },
                    React.createElement("span", { className: styles.iconButton },
                        React.createElement(Button, { testId: 'button-close', onClick: function () { return setFilterOpen(!filterOpen); }, mode: ButtonMode.text, iconOnly: true, noShadowOnHover: true, subtleShadeOnHover: true },
                            React.createElement(CloseIcon, { color: iconLight }))))),
            React.createElement(PharmacyRequestsSummaryFilter, __assign({}, filterState, { pharmacyRequestTerm: pharmacyRequestTerm, encounterPlaceTerm: encounterPlaceTerm }))));
    };
    var mapToRequest = function (service) {
        return { service: service };
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.contents },
            React.createElement(TwoPanelLayout, { leftPanelOpen: filterOpen && !pharmacyRequestHistory, noPadding: true, contentsLeft: true, mask: true, leftPanel: getLeftPanel(), contents: getContents() })),
        (pharmacyRequestWizardView === PharmacyRequestWizardView.NewPharmacyRequestWizard ||
            !!displayNewPharmacyRequestWizard) && (React.createElement(AddPharmacyRequestForm, { animationStyle: animationStyle, headlessUpdate: {
                onSuccess: function (response) {
                    var _a;
                    if ((_a = props.headlessPharmacyRequest) === null || _a === void 0 ? void 0 : _a.onSuccess) {
                        props.headlessPharmacyRequest.onSuccess(response);
                    }
                    else {
                        displayMessage(response.message);
                    }
                    filterState.refresh();
                    nagivateBackTo();
                },
                onFailure: function (response) {
                    var _a;
                    if ((_a = props.headlessPharmacyRequest) === null || _a === void 0 ? void 0 : _a.onFailure) {
                        props.headlessPharmacyRequest.onFailure(response);
                    }
                    else {
                        displayError(response.message);
                    }
                    nagivateBackTo();
                }
            }, getMedicationsToRequest: function () {
                return getMedicationsToRequestByPatientAuthKeys(props.patient.authoringSystem, props.patient.authoringSystemForeignKey, mapToRequest(props.service));
            }, onCreatePharmacyRequest: getOnCreatePharmacyRequest(props), onClose: function () {
                nagivateBackTo();
            }, onSuccess: function () {
                filterState.refresh();
                nagivateBackTo();
            }, goToMedications: function () { return props.routeToMedications(); } }))));
};
